import {
  IssueCaseDoc,
  IssueCaseIssuedByType,
  IssueCaseRequestSubcategory,
  IssueCaseSubcategory,
} from '@gooduncles/gu-app-schema';
import { Image, Input, Select, Spin, message, notification } from 'antd';
import { FC } from 'react';

import { errorObjectToString } from 'src/utils/common';
import { addImageToIssueCase, removeImageFromIssueCase, updateIssueCase } from 'src/utils/firebase/firebase-short-cut';
import { UploadImageResult } from 'src/utils/image-util';
import { getSubcategoryLabel } from 'src/utils/issue/issue-data-util';
import { issueCaseIssuedByMap, issueCaseIssuedByOptions, issueCaseSubOptions } from 'src/utils/issue/issue-util';

import useUserById from 'src/hooks/swr/useUserById';
import useDebouncedUpdate from 'src/hooks/useDebouncedUpdate';

import { InfoItem } from 'src/atomic-components/atoms/IssueInfoItem';

import ImageUploadItem from '../IssueImageUploadItem';

interface IssueCaseInfoSectionProps {
  issueCaseDoc: IssueCaseDoc;
  editable: boolean;
}

const IssueCaseInfoSection: FC<IssueCaseInfoSectionProps> = ({ issueCaseDoc, editable }) => {
  const { user, userLoading } = useUserById(issueCaseDoc.creatorId);
  const subcategoryOptions = issueCaseSubOptions(issueCaseDoc.category);

  // 로컬 상태 + debounced 업데이트를 함께 관리
  const [privateNote, setPrivateNote] = useDebouncedUpdate(
    issueCaseDoc.privateNote ?? '',
    async (value: string) => {
      try {
        await updateIssueCase(issueCaseDoc._id, { privateNote: value || null });
      } catch (error) {
        console.error('Failed to update privateNote:', error);
      }
    },
    1000,
  );

  // TODO: 추후 공개 메모 기능 추가 시 사용
  // const [publicNote, setPublicNote] = useDebouncedUpdate(
  //   issueCaseDoc.publicNote ?? '',
  //   async (value: string) => {
  //     try {
  //       await updateIssueCase(issueCaseDoc._id, { publicNote: value || null });
  //     } catch (error) {
  //       console.error('Failed to update publicNote:', error);
  //     }
  //   },
  //   1000
  // );

  const images = issueCaseDoc.images ?? {};

  const onSubcategoryChange = async (value: IssueCaseSubcategory | IssueCaseRequestSubcategory) => {
    try {
      await updateIssueCase(issueCaseDoc._id, { subcategory: value });
    } catch (error) {
      console.error(error);
      notification.error({ message: '상세 분류 변경에 실패했습니다.' });
    }
  };

  const onIssuedByChange = async (value: IssueCaseIssuedByType) => {
    try {
      await updateIssueCase(issueCaseDoc._id, { issuedBy: value });
    } catch (error) {
      console.error(error);
      notification.error({ message: '제기 유형 변경에 실패했습니다.' });
    }
  };

  const onImageUploadSuccess = async (result: UploadImageResult, index: number) => {
    try {
      await addImageToIssueCase(issueCaseDoc._id, result.imageFileId, {
        ...result.result,
        index,
      });
      message.success('이미지 업로드 성공');
    } catch (error) {
      console.error(error);
      notification.error({ message: '이미지 업로드 실패', description: errorObjectToString(error) });
    }
  };

  const onImageDeleteSuccess = async (imageId: string) => {
    try {
      await removeImageFromIssueCase(issueCaseDoc._id, imageId);
    } catch (error) {
      console.error('Failed to update images after deletion:', error);
      notification.error({
        message: '이미지 삭제 후 업데이트 실패',
        description: errorObjectToString(error),
      });
    }
  };

  return (
    <div>
      <>
        <InfoItem
          label='작성자'
          value={userLoading ? <Spin size='small' /> : user?.email?.split('@')[0] ?? issueCaseDoc.creatorType}
        />
        {editable ? (
          <InfoItem
            label='이슈 출처'
            value={
              <Select
                size='small'
                value={issueCaseDoc.issuedBy}
                onChange={onIssuedByChange}
                style={{ width: 180 }}
                options={issueCaseIssuedByOptions}
                placeholder='선택'
              />
            }
          />
        ) : issueCaseDoc.issuedBy ? (
          <InfoItem label='이슈 출처' value={issueCaseIssuedByMap[issueCaseDoc.issuedBy]} />
        ) : null}
        {subcategoryOptions && editable ? (
          <InfoItem
            label='분류'
            value={
              <Select
                size='small'
                value={issueCaseDoc.subcategory}
                onChange={onSubcategoryChange}
                style={{ width: 180 }}
                options={subcategoryOptions}
              />
            }
          />
        ) : issueCaseDoc.subcategory ? (
          <InfoItem label='분류' value={getSubcategoryLabel(issueCaseDoc.category, issueCaseDoc.subcategory)} />
        ) : null}
        {issueCaseDoc.content ? <InfoItem label='내용' value={issueCaseDoc.content} vertical /> : null}
        {/* TODO: 추후 공개 메모 기능 추가 시 사용 <InfoItem
          label='고객노출 메모'
          value={
            editable ? (
              <Input
                size='small'
                placeholder='상세내용을 입력하세요'
                value={publicNote}
                onChange={(e) => setPublicNote(e.target.value)}
                allowClear
                style={{ width: 216 }}
              />
            ) : (
              <span>{publicNote}</span>
            )
          }
        /> */}
      </>

      {/* Memo */}
      <>
        <InfoItem
          label='내부 메모'
          value={
            editable ? (
              <Input
                size='small'
                placeholder='내부 메모'
                value={privateNote}
                allowClear
                onChange={(e) => setPrivateNote(e.target.value)}
                style={{ width: 216 }}
              />
            ) : (
              <span>{privateNote}</span>
            )
          }
        />
      </>

      {/* Image Upload */}
      <>
        {editable ? (
          <ImageUploadItem
            label='이슈 사진'
            maxCount={3}
            images={images}
            owner={{ entity: 'issueCase', caseId: issueCaseDoc._id }}
            onUploadSuccess={onImageUploadSuccess}
            onDeleteSuccess={onImageDeleteSuccess}
          />
        ) : (
          <div className='flexRow'>
            {Object.entries(images).map(([imageId, image]) => (
              <Image
                key={imageId}
                width={100}
                height={100}
                src={image.thumbnailUrl}
                preview={{
                  src: image.originalUrl,
                }}
                style={{ borderRadius: 4, border: '1px solid #e0e0e0', padding: 4 }}
              />
            ))}
          </div>
        )}
      </>
    </div>
  );
};

export default IssueCaseInfoSection;
export type { IssueCaseInfoSectionProps };
